import styled, { css, useTheme } from "styled-components";
import { Copy, Info, RefreshCw } from "lucide-react";
import {
  NEW_LOOK_ICON_STROKE,
  NEW_LOOK_PAGE_GUTTER,
  NEW_LOOK_PAGE_GUTTER_VAR,
} from "common/NewLook/layout.ts";
import Tag from "common/Tags/Tag.tsx";
import {
  extractTelegramUsernameFromLink,
  hexToRGB,
} from "@shared/utils/misc.ts";
import Text from "common/Typography/Text.tsx";
import React, { useEffect, useState } from "react";
import Flex from "common/Boxes/Flex";
import Button from "common/Buttons/Button";
import { SyncOutlined } from "@ant-design/icons";
import CopyOutlined from "@ant-design/icons/CopyOutlined";
import { copyCodeToClipboard, openMessage } from "utils/misc.tsx";
import { getCopyMessage } from "@shared/common/CopyToClipboard/utils.ts";
import { sharedLib } from "@shared/services/shared.service.ts";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";
import { skipToken } from "@reduxjs/toolkit/query";
import { useHistory } from "react-router-dom";
import { MY_GROUPS_PATH } from "common/constants.ts";
import { useDispatch, useSelector } from "react-redux";
import {
  ApiTags,
  ExperienceConnectionRequestStatus,
  PlatformType,
  SubGroupType,
} from "@shared/types";
import Link from "common/Typography/Link.tsx";
import WhopChannelTypeTabs from "features/whop/WhopChannelTypeTabs.tsx";
import HighlightedText from "common/Text/HighlightedText";
import InfoCircleOutlined from "@ant-design/icons/InfoCircleOutlined";
import usePersonalGroupModal from "features/bots/Groups/usePersonalGroupModal.tsx";
import IntercomLink from "common/Text/IntercomLink.tsx";

const ConnectWhopChannel = () => {
  const theme = useTheme();
  const history = useHistory();
  const [platformType, setPlatformType] = useState(PlatformType.TELEGRAM);
  const [
    generateExperienceConnectionRequest,
    { isLoading, data: experienceCode },
  ] =
    sharedLib.sharedApi.settings.useGenerateExperienceConnectionRequestMutation();
  const { data: checkData } =
    sharedLib.sharedApi.settings.useCheckExperienceConnectionRequestQuery(
      experienceCode && platformType !== PlatformType.CORNIX
        ? { code: experienceCode }
        : skipToken,
      { pollingInterval: 5000 }
    );
  const [lastCode, setLastCode] = useState<string>();
  const dispatch = useDispatch();
  const user = useSelector(sharedLib.selectors.authentication.selectRealUser);

  const onSuccess = () => {
    if (platformType !== PlatformType.CORNIX) {
      openMessage("Group successfully connected!", "success");
    }
    dispatch(
      sharedLib.slices.authentication.actions.updateUser({
        key: "has_connected_whop_groups",
        value: true,
      })
    );
    dispatch(
      sharedLib.sharedApi.admin.util.invalidateTags([
        { type: ApiTags.ADMIN_SUB_GROUPS, id: "LIST" },
      ])
    );
    dispatch(
      sharedLib.sharedApi.admin.util.invalidateTags([
        { type: ApiTags.GROUPS, id: "LIST" },
      ])
    );
    history.push(MY_GROUPS_PATH);
  };

  const {
    isLoading: isLoadingCreate,
    isDisabled,
    onOkClick,
    nameInput,
    errorMessage,
  } = usePersonalGroupModal({ subGroupType: SubGroupType.PREMIUM, onSuccess });

  useEffect(() => {
    if (platformType !== PlatformType.CORNIX && !experienceCode) {
      generateExperienceConnectionRequest();
    }
  }, [platformType]);

  useEffect(() => {
    if (checkData?.status === ExperienceConnectionRequestStatus.USED) {
      onSuccess();
    } else if (
      checkData?.status === ExperienceConnectionRequestStatus.EXPIRED
    ) {
      generateExperienceConnectionRequest();
    }
  }, [checkData]);

  useEffect(() => {
    if (experienceCode) {
      setLastCode(experienceCode);
    }
  }, [experienceCode]);

  const onCopyClick = () => {
    if (experienceCode) {
      copyCodeToClipboard(experienceCode).catch(() => {});
      openMessage(getCopyMessage("Code"), "info");
    }
  };

  const getFirstStep = () => {
    const userBotUsername = extractTelegramUsernameFromLink(user?.bot_url);
    if (platformType === PlatformType.CORNIX) {
      return (
        <Card>
          <CardTitleWrapper>Create Cornix Group</CardTitleWrapper>
          <CardRow>
            <div>
              <Text>
                Choose a name for your Cornix group and press the Create Group
                button below.
              </Text>
              <Text style={{ marginBottom: 12 }}>
                This name will be used to identify the group for you and your
                users inside Cornix.
              </Text>
              <Text style={{ margin: "12px 0 4px 0" }}>Group Name:</Text>
              {nameInput}
              {errorMessage}
            </div>
            <Button
              type="primary"
              $disabled={isDisabled}
              onClick={onOkClick}
              loading={isLoadingCreate}
              style={{ marginTop: 20 }}
            >
              Create Group
            </Button>
          </CardRow>
        </Card>
      );
    }
    if (platformType === PlatformType.TELEGRAM) {
      return (
        <Card>
          <CardTitleWrapper>
            Step 1: Add Cornix Bot to Your Telegram Channel
          </CardTitleWrapper>
          <CardRow>
            Add the Cornix Bot (
            <Link
              href={user?.bot_url}
              target="_blank"
              style={{ fontSize: "inherit", textDecoration: "none" }}
            >
              @{userBotUsername}
            </Link>
            ) as a Channel Admin and follow the instructions.
          </CardRow>
        </Card>
      );
    }
    return (
      <Card>
        <CardTitleWrapper>
          Step 1: Add Cornix Bot to your Discord
        </CardTitleWrapper>
        <CardRow>
          <div>
            Click the button below to add the Cornix Trading Bot to your Discord
            server.
          </div>
          <Button
            type="primary"
            onClick={() =>
              window.open(
                "https://www.cornix.io/connect_discord_bot/",
                "_blank"
              )
            }
            style={{ marginTop: 12 }}
          >
            Add Cornix Bot to Discord
          </Button>
        </CardRow>
      </Card>
    );
  };

  const getVerifyStep = () => {
    if (platformType === PlatformType.CORNIX) return;
    return (
      <Card>
        <CardTitleWrapper>Step 2: Verify</CardTitleWrapper>
        <CardRow>
          <div>
            Paste the code in your channel once the Cornix bot is added to it.
          </div>
          <CodeWrapper>
            <LoadingSpinnerOverlay
              isLoading={!lastCode}
              hideContentWhileLoading={true}
              wrapStyle={{ width: "100%" }}
            >
              <CodeRow>
                <Tag
                  color={theme.colors.primaryGrey}
                  bgColor={hexToRGB(theme.colors.secondaryGrey, 0.1)}
                  style={{ maxWidth: "100%" }}
                >
                  <Text $ellipsis={true}>{lastCode}</Text>
                </Tag>
                <Flex style={{ columnGap: 8 }}>
                  <Button
                    onClick={() => generateExperienceConnectionRequest()}
                    loading={isLoading}
                  >
                    {theme.isNewLook ? (
                      <RefreshCw size={14} strokeWidth={NEW_LOOK_ICON_STROKE} />
                    ) : (
                      <SyncOutlined />
                    )}
                    Refetch Code
                  </Button>
                  <Button
                    type="primary"
                    onClick={onCopyClick}
                    loading={isLoading}
                  >
                    {theme.isNewLook ? (
                      <Copy size={14} strokeWidth={NEW_LOOK_ICON_STROKE} />
                    ) : (
                      <CopyOutlined />
                    )}
                    Copy Code
                  </Button>
                </Flex>
              </CodeRow>
            </LoadingSpinnerOverlay>
          </CodeWrapper>
          <Waiting>
            <SyncOutlined style={{ marginRight: 8 }} spin={true} />
            Waiting...
          </Waiting>
        </CardRow>
      </Card>
    );
  };

  const learnHowLink = (
    <IntercomLink articleId="13440433" style={{ textDecoration: "underline" }}>
      Learn how it works
    </IntercomLink>
  );

  if (theme.isNewLook) {
    return (
      <Wrapper>
        <WhopChannelTypeTabs
          platformType={platformType}
          setPlatformType={setPlatformType}
        />
        <NewLookNote>
          <Info size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
          <span>
            Let your Whop group members follow your Cornix signals
            automatically. {learnHowLink}
          </span>
        </NewLookNote>
        {getFirstStep()}
        {getVerifyStep()}
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <WhopChannelTypeTabs
        platformType={platformType}
        setPlatformType={setPlatformType}
        style={{ width: "100%", maxWidth: 500, columnGap: 16 }}
        iconStyle={{ fontSize: 20 }}
      />
      <HighlightedText hideLine={true} style={{ padding: 12 }}>
        <Flex $alignItems="flex-start">
          <InfoCircleOutlined
            style={{
              marginRight: 12,
              flex: "none",
              fontSize: 20,
              marginTop: 4,
            }}
          />
          <span style={{ flex: 1, display: "block" }}>
            Let your Whop group members follow your Cornix signals
            automatically.
            <br />
            <IntercomLink
              articleId="13440433"
              style={{ textDecoration: "underline" }}
            >
              Learn how it works
            </IntercomLink>
          </span>
        </Flex>
      </HighlightedText>
      {getFirstStep()}
      {getVerifyStep()}
    </Wrapper>
  );
};

export default ConnectWhopChannel;

const Wrapper = styled.div`
  margin: 0 auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  width: 100%;
  max-width: 800px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin: 0;
      padding: 20px ${NEW_LOOK_PAGE_GUTTER_VAR} ${NEW_LOOK_PAGE_GUTTER_VAR};
      max-width: ${760 + NEW_LOOK_PAGE_GUTTER * 2}px;
      @media (max-width: 450px) {
        padding: 16px;
      }
    `}
`;
const Card = styled.div`
  border: ${(p) => p.theme.borders.medium};
  border-radius: 8px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      border: ${p.theme.borders.panel};
      border-radius: ${p.theme.radii.card}px;
      background: ${p.theme.colors.cardBgColor};
      box-shadow: ${p.theme.shadows.panel};
    `}
`;
const CardTitleWrapper = styled.div`
  color: ${(p) => p.theme.colors.primaryText};
  background-color: ${(p) => p.theme.colors.statisticsBgColor};
  border-bottom: ${(p) => p.theme.borders.medium};
  border-radius: 8px 8px 0 0;
  padding: 16px;
  font-size: 16px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      color: ${p.theme.colors.titleColor};
      background: transparent;
      border-bottom: none;
      padding: 18px 20px 0;
      font-size: 15px;
      font-weight: ${p.theme.typography.fontWeights.BOLD};
    `}
`;
const CardRow = styled.div`
  color: ${(p) => p.theme.colors.secondaryText};
  &:not(:last-of-type) {
    border-bottom: ${(p) => p.theme.borders.medium};
  }
  padding: 16px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      padding: 6px 20px 20px;
      font-size: 13.5px;
      line-height: 1.55;
    `}
`;
const CodeWrapper = styled.div`
  padding: 0 12px;
  border: ${(p) => p.theme.borders.medium};
  border-radius: 8px;
  margin-top: 12px;
  position: relative;
  height: 58px;
  display: flex;
  align-items: center;
  width: 100%;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      height: auto;
      min-height: 58px;
      padding: 8px 8px 8px 12px;
      border: ${p.theme.borders.lightest};
      border-radius: 12px;
      background: ${p.theme.colors.cardLightBgColor};
    `}
`;
const CodeRow = styled(Flex)`
  ${(p) =>
    p.theme.isNewLook &&
    css`
      flex-wrap: wrap;
      gap: 8px;
    `}
`;
const Waiting = styled.div`
  margin-top: 12px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      font-size: 12.5px;
      color: ${p.theme.colors.secondaryText};
    `}
`;
const NewLookNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.lightBlue};
  color: ${(p) => p.theme.colors.primaryText};
  font-size: 13.5px;
  line-height: 1.5;
  svg {
    flex: none;
    margin-top: 2px;
    color: ${(p) => p.theme.colors.primaryBlue};
  }
`;
